import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// Our components
import Notification from '../../../pages/General/Notification';
import NorowsData from '../../../pages/General/NorowsData';
import Date from '../../../pages/General/Date';

// mui components
import {
    FormControl,
    Pagination,
    IconButton,
    Button,
    Tooltip,
    Modal,
    Radio,
    Box,
    Typography,
    FormControlLabel,
    RadioGroup,
    Grid,
    Stack
} from "@mui/material";
import LinearProgress from '@mui/material/LinearProgress';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';

import {
    fetchInvoice,
    resetInvoiceNotifications,
    instantChargeInvoice
} from './invoiceSlice';
import { getSubscription } from '../Payment/paymentSlice';
import Filters from './Filters';
import InvoiceDetailDrawer from '../../../pages/General/InvoiceDetailDrawer';

// Global enterprise styling dictionary configuring standard outlined input look and feel
const enterpriseInvoiceInputSelectStyles = {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
    padding: '6px 10px',
    fontSize: '13px',
    color: '#334155',
    fontWeight: 600,
    outline: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
        borderColor: '#CBD5E1',
        backgroundColor: '#F8FAFC'
    },
    '&:focus': {
        borderColor: '#1976d2',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
    }
};

const InvoicePage = () => {
    const dispatch = useDispatch();
    const invoice = useSelector(state => state.invoice.invoiceData);
    const activePaymentData = useSelector(state => state.payment.getActivePayment);
    const instantCharge = useSelector(state => state.invoice.instantCharge);
    const profile = useSelector(state => state.auth);
    const [meta, setMeta] = useState(invoice.meta);
    const [showFilter, setShowFilter] = useState(false);
    const [invoiceId, setInvoiceId] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [invoiceDetailDrawerVisible, setInvoiceDetailDrawerVisible] = useState(false);
    const [open, setOpen] = useState(false);
    const [csvType, setCsvType] = useState('standard');
    const [standardUrl, setStandardUrl] = useState(null);
    const [extendedUrl, setExtendedUrl] = useState(null);

    const handleOpen = (simplified_invoice_shipment_details_csv_url, detailed_invoice_shipment_details_csv_url) => {
        setOpen(true);
        setStandardUrl(simplified_invoice_shipment_details_csv_url);
        setExtendedUrl(detailed_invoice_shipment_details_csv_url);
    };
     const handleClose = () => {
        setOpen(false);
        setCsvType('standard');
        setStandardUrl(null);
        setExtendedUrl(null);
    };
    const handleCsvTypeChange = (event) => {
        console.log(event.target.value);
        setCsvType(event.target.value);
    };
    const handleDownload = () => {
        if (csvType === 'standard'  && standardUrl) {
            console.log(standardUrl);
            window.open(standardUrl, '_blank');
        } else if (csvType === 'extended' && extendedUrl) {
            console.log(extendedUrl);
            window.open(extendedUrl, '_blank');
        }
        handleClose();
    };
    let columns = [
        {
            field: 'invoice_number',
            headerName: 'Invoice Number #',
            minWidth: 200,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography 
                        variant="body2" 
                        onClick={e => { invoiceCallback(params.row.id, params.row.invoice_number) }}
                        sx={{ 
                            color: '#1976d2', 
                            cursor: 'pointer', 
                            fontWeight: 700,
                            fontSize: '13.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '100%',
                            '&:hover': { textDecoration: 'underline', color: '#115293' }
                        }}
                    >
                        {params.row.invoice_number}
                    </Typography>
                );
            }
        },
        {
            field: 'invoiced_date',
            headerName: 'Invoiced Date',
            minWidth: 160,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '13.5px', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                        {params.row && params.row.invoiced_date ? params.row.invoiced_date : "-"}
                    </Typography>
                );
            }
        },
        {
            field: 'invoice_total_amount_converted',
            headerName: 'Amount',
            minWidth: 150,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                let symbol = (params.row && params.row.currency_symbol) ? params.row.currency_symbol : "";
                return (
                    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '14px', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                        {params.row && params.row.invoice_total_amount_converted ? symbol + params.row.invoice_total_amount_converted : "-"}
                    </Typography>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Downloads',
            minWidth: 160,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row && params.row.invoice_pdf_url && params.row.invoice_pdf_url != "") {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            <Tooltip title="Download Invoice" placement="top">
                                <IconButton
                                    onClick={() => window.open(params.row.invoice_pdf_url, '_blank')}
                                    sx={{ 
                                        color: '#1976d2', padding: '6px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' }
                                    }}
                                >
                                    <DescriptionIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Invoice Shipment Details" placement="top">
                                <IconButton
                                    onClick={() => handleOpen(params.row.simplified_invoice_shipment_details_csv_url, params.row.detailed_invoice_shipment_details_csv_url)}
                                    sx={{ 
                                        color: '#1976d2', padding: '6px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' }
                                    }}
                                >
                                    <LocalShippingOutlinedIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                } else {
                    return <Typography variant="body2" sx={{ color: '#94A3B8', display: 'inline-flex', alignItems: 'center', height: '100%' }}>-</Typography>;
                }
            }
        },
        {
            field: 'Payment',
            headerName: 'Payment',
            minWidth: 200,
            flex: 1.2,
            sortable: false,
            renderCell: (params) => {
                if (params.row.is_paid == true && params.row && params.row.invoice_pdf_url && params.row.invoice_pdf_url != "") {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                            <Button 
                                variant="contained" 
                                disabled={params.row && params.row.is_paid}
                                sx={{ 
                                    textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '11px', px: 2, height: '26px', minWidth: '65px', boxShadow: 'none',
                                    backgroundColor: '#2E7D32', color: '#FFFFFF', '&.Mui-disabled': { backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#2E7D32' }
                                }}
                            >
                                PAID
                            </Button>
                            { (params.row && params.row.is_paid && params.row.payment_invoice_url != "" && params.row.payment_invoice_url != null) &&
                                <Tooltip title="View Receipt" placement="top">
                                    <IconButton
                                        onClick={() => window.open(params.row.payment_invoice_url, "_blank")}
                                        sx={{ 
                                            color: '#64748B', padding: '5px', borderRadius: '6px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF',
                                            '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' }
                                        }}
                                    >
                                        <RemoveRedEyeIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                </Tooltip>
                            }
                        </Box>
                    );
                } else if(params.row.is_paid == false && activePaymentData && activePaymentData.data && activePaymentData.data.length != 0) {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Button 
                                variant="contained" 
                                disabled={(instantCharge && instantCharge.loading) || (params.row.invoice_total_amount_converted && params.row.invoice_total_amount_converted < 0)} 
                                onClick={() => onPayClick(params.row && params.row.id && params.row.id)}
                                sx={{ 
                                    textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '11px', px: 2.5, height: '26px', minWidth: '65px', boxShadow: 'none',
                                    backgroundColor: '#ED6C02', color: '#FFFFFF', '&:hover': { backgroundColor: '#E65100', boxShadow: 'none' },
                                    '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                }}
                            >
                                PAY
                            </Button>
                        </Box>
                    );
                } else if((profile && profile.profile && profile.profile.payment_method && profile.profile.payment_method==='CC' || profile.profile.payment_method===null) && params.row.is_paid == false && activePaymentData && activePaymentData.data && activePaymentData.data.length == 0) {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Link to="/settings/payment" style={{ textDecoration: 'none' }}>
                                <Button 
                                    variant="outlined"
                                    sx={{ 
                                        textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '11px', px: 2, height: '26px',
                                        borderColor: '#1976d2', color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)', borderColor: '#115293' }
                                    }}
                                >
                                    Add Payment
                                </Button>
                            </Link>
                        </Box>
                    );
                } else if(profile && profile.profile && profile.profile.payment_method && profile.profile.payment_method !=='CC' && params.row.is_paid == false && activePaymentData && activePaymentData.data && activePaymentData.data.length == 0) {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Button 
                                variant="contained" 
                                disabled={(instantCharge && instantCharge.loading)}
                                sx={{ 
                                    textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '11px', px: 2, height: '26px', minWidth: '75px', boxShadow: 'none',
                                    backgroundColor: '#0288D1', color: '#FFFFFF', '&.Mui-disabled': { backgroundColor: 'rgba(2, 136, 209, 0.12)', color: '#0288D1' }
                                }}
                            >
                                PENDING
                            </Button>
                        </Box>
                    );
                } else {
                    return <Typography variant="body2" sx={{ color: '#94A3B8', display: 'inline-flex', alignItems: 'center', height: '100%' }}>-</Typography>;
                }
            }
        }
    ];

    useEffect(() => {
        dispatch(getSubscription(""));
        dispatch(fetchInvoice({ ...meta }));
    }, [meta]);

    const onExportClick = (invoice_id, invoice_number) => {
        let url = process.env.REACT_APP_API_DOMAIN + '/labelgen/invoice-shipments/export?invoice_id=' + invoice_id+'&invoice_number='+invoice_number;
        window.open(url, '_blank');
    }

    const onPayClick = (invoice_id) => {
        dispatch(instantChargeInvoice({ "invoice_id": invoice_id }, { ...meta }));
    }

    const invoiceCallback = (invoiceid, invoice_number) => {
        setInvoiceId(invoiceid);
        setInvoiceNumber(invoice_number);
        setInvoiceDetailDrawerVisible(true);
    }

    const invoiceDetailOnClose = () => {
        setInvoiceDetailDrawerVisible(false);
    }

    function CustomTotalRows() {
        return (
            <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, fontSize: '13px' }}>
                Showing {invoice && invoice.data && invoice.data.length} of {(invoice && invoice.meta && invoice.meta.total) ? invoice.meta.total : 0}
            </Typography>
        );
    }

    const onChangePerPage = (event) => {
        setMeta({
            ...meta,
            ...{
                page: 1,
                per_page: event.target.value
            }
        });
    }

    const onChangePagination = (event, page) => {
        setMeta({
            ...meta,
            ...{
                page: page
            }
        });
    }

    function CustomPagesizeoptions() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, fontSize: '13px' }}>
                    Show
                </Typography>
                <select
                    value={meta && meta.per_page}
                    onChange={onChangePerPage}
                    style={enterpriseInvoiceInputSelectStyles}
                >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600, fontSize: '13px' }}>
                    entries
                </Typography>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                <Pagination
                    color="primary"
                    shape="circular"
                    size="small"
                    page={invoice && invoice.meta && invoice.meta.current_page}
                    count={invoice && invoice.meta && invoice.meta.last_page}
                    onChange={onChangePagination}
                    sx={{
                        '& .MuiPaginationItem-root': { fontWeight: 600 },
                        '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#1976d2', color: '#FFFFFF' }
                    }}
                />
            </Box>
        );
    }

    const onMenuClick = (type, show) => {
        setShowFilter((type === "filter" && show === true) ? true : false);
    }

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box',p:2.5 }}>
            {(invoice && invoice.notification && invoice.notification.show && invoice.notification.messageContent && invoice.notification.messageContent !== "") &&
                <Notification
                    is_open={invoice.notification.show}
                    messageType={invoice.notification.messageType}
                    messages={invoice.notification.messageContent}
                    handleClose={() => dispatch(resetInvoiceNotifications())}
                />
            }
            {(instantCharge && instantCharge.notification && instantCharge.notification.show && instantCharge.notification.messageContent && instantCharge.notification.messageContent !== "") &&
                <Notification
                    is_open={instantCharge.notification.show}
                    messageType={instantCharge.notification.messageType}
                    messages={instantCharge.notification.messageContent}
                    handleClose={() => dispatch(resetInvoiceNotifications())}
                />
            }

            {/* Modal: Download Format Option Dialog Intercept */}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
                closeAfterTransition
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }}
            >
                <Box
                    sx={{
                        backgroundColor: '#FFFFFF',
                        padding: 3.5,
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        flexDirection: 'column',
                        width: { xs: '92%', sm: 400 },
                        outline: 'none'
                    }}
                >
                    <Typography id="modal-title" variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', mb: 2, fontSize: '16px', textAlign: 'center' }}>
                        Download CSV Report
                    </Typography>
                    
                    <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', mb: 3 }}>
                        <RadioGroup
                            aria-labelledby="csv-type-group"
                            name="csv-type"
                            value={csvType}
                            onChange={handleCsvTypeChange}
                            sx={{ gap: 1.5 }}
                        >
                            <FormControlLabel 
                                value="standard" 
                                control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                                label={<Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Standard CSV Template</Typography>} 
                            />
                            <FormControlLabel 
                                value="extended" 
                                control={<Radio size="small" sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1976d2' } }} />} 
                                label={<Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Extended CSV Layout</Typography>} 
                            />
                        </RadioGroup>
                    </Box>

                    <Stack direction="row" spacing={1.5} justifyContent="center" width="100%">
                        <Button 
                            variant="outlined" 
                            onClick={handleClose}
                            sx={{
                                textTransform: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '13.5px', height: '38px', flex: 1,
                                borderColor: '#E2E8F0', color: '#475569', '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleDownload}
                            sx={{
                                textTransform: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '13.5px', height: '38px', flex: 1, boxShadow: 'none',
                                backgroundColor: '#1976d2', color: '#FFFFFF', '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                            }}
                        >
                            Download
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            {/* Core Card Document Presentation Surface Workspace */}
            <Card 
                className="sc--cardhead--details"
                sx={{ 
                    width: '100%', 
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <CardHeader 
                    title={
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px', margin: 0 }}>
                            Invoices
                        </Typography>
                    } 
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 0.5 }}>
                            <Tooltip title="Back to Billing" placement="top">
                                <Link to="/settings/billing" style={{ textDecoration: 'none' }}>
                                    <IconButton 
                                        sx={{ 
                                            color: '#64748B', borderRadius: '8px', border: '1px solid #E2E8F0', p: 1, transition: 'all 0.15s ease-in-out',
                                            '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B', borderColor: '#CBD5E1' }
                                        }}
                                    >
                                        <ArrowBackIosNewIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                </Link>
                            </Tooltip>
                            <Tooltip title="Toggle Filters" placement="top">
                                <IconButton 
                                    onClick={() => onMenuClick("filter", !showFilter)}
                                    sx={{ 
                                        color: showFilter ? '#1976d2' : '#64748B', borderRadius: '8px', border: '1px solid',
                                        borderColor: showFilter ? 'rgba(25, 118, 210, 0.2)' : '#E2E8F0', p: 1, transition: 'all 0.15s ease-in-out',
                                        backgroundColor: showFilter ? 'rgba(25, 118, 210, 0.04)' : '#FFFFFF',
                                        '&:hover': { backgroundColor: showFilter ? 'rgba(25, 118, 210, 0.08)' : '#F1F5F9', color: showFilter ? '#115293' : '#1E293B' }
                                    }}
                                >
                                    <FilterAltOutlinedIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />

                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box className="scmaincard" sx={{ width: '100%', background: '#FFFFFF' }}>
                        
                        {/* Dynamic Accordion Filter Context Panel */}
                        {showFilter && (
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                                <Filters visible={showFilter} meta={meta} setMeta={setMeta} />
                            </Box>
                        )}

                        {/* Interactive Invoices Ledger Table Engine */}
                        <Box sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                            <DataGrid
                                sx={{
                                    border: 'none',
                                    backgroundColor: '#FFFFFF',
                                    overflow: 'hidden',
                                    '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                                    '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                    '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' } },
                                    '& .MuiDataGrid-cell': { borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                                    '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                                }}
                                getRowId={(row) => row.id}
                                rows={invoice && invoice.data || []}
                                columns={columns}
                                autoHeight={true}
                                disableColumnFilter={true}
                                disableColumnMenu={true}
                                disableRowSelectionOnClick
                                slots={{
                                    loadingOverlay: LinearProgress,
                                    pagination: CustomPagination,
                                    toolbar: CustomToolbar,
                                    noRowsOverlay: NorowsData
                                }}
                                loading={
                                    !!((invoice && invoice.loading) || (instantCharge && instantCharge.loading) || (activePaymentData && activePaymentData.loading))
                                }
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card >
            
            {/* Sliding Drawer Context Segment Detail Component */}
            {invoiceId && (
                <InvoiceDetailDrawer
                    visible={invoiceDetailDrawerVisible} 
                    invoiceId={invoiceId} 
                    invoiceNumber={invoiceNumber} 
                    invoiceDetailOnClose={invoiceDetailOnClose}
                />
            )}
        </Box>
    );
};

export default InvoicePage;